/**
 * useIsMobile Hook
 * Task 7.4.13: Mobile Responsiveness
 *
 * Detects if the viewport is at or below a mobile breakpoint.
 * Default breakpoint: 480px (as per requirements).
 */

import { useState, useEffect } from 'react';

const DEFAULT_MOBILE_BREAKPOINT = 480;

/**
 * Hook to detect if the viewport is mobile-sized.
 *
 * @param breakpoint - The max-width breakpoint in pixels (default: 480)
 * @returns true if viewport width <= breakpoint
 */
export function useIsMobile(breakpoint: number = DEFAULT_MOBILE_BREAKPOINT): boolean {
  const query = `(max-width: ${breakpoint}px)`;

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);

    const handleChange = (e: MediaQueryListEvent | { matches: boolean }) => {
      setIsMobile(e.matches);
    };

    // Set initial value
    setIsMobile(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query]);

  return isMobile;
}
